import {
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';

const jumbotronAnimation = {
  '0%': {
    backgroundPosition: '0% 50%',
  },
  '50%': {
    backgroundPosition: '100% 50%',
  },
  '100%': {
    backgroundPosition: '0% 50%',
  },
};

const lightPulse = {
  '0%, 100%': {
    opacity: 0.3,
  },
  '50%': {
    opacity: 0.6,
  },
};

export const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  jumbotron: {
    position: 'relative',
    background: 'linear-gradient(-35deg, #113c69, #1b5b99, #257dca, #52a3ea)',
    backgroundSize: '400% 400%',
    animationName: jumbotronAnimation,
    animationDuration: '15s',
    animationTimingFunction: 'ease',
    animationIterationCount: 'infinite',
    color: '#ffffff',
    textAlign: 'center',
    ...shorthands.padding('60px', '20px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    boxShadow: tokens.shadow16,
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
        linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px)
      `,
      backgroundSize: '50px 50px',
      zIndex: 0,
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        linear-gradient(135deg, transparent 0%, rgba(30, 105, 245, 0.15) 30%, transparent 60%),
        linear-gradient(-45deg, transparent 20%, rgba(30, 105, 245, 0.1) 50%, transparent 80%),
        linear-gradient(45deg, transparent 40%, rgba(255, 255, 255, 0.05) 60%, transparent 75%),
        linear-gradient(155deg, rgba(30, 105, 245, 0.08) 0%, transparent 40%)
      `,
      animationName: lightPulse,
      animationDuration: '4s',
      animationTimingFunction: 'ease-in-out',
      animationIterationCount: 'infinite',
      zIndex: 0,
      pointerEvents: 'none',
    },
  },
  jumbotronContent: {
    position: 'relative',
    zIndex: 2,
  },
  jumbotronTitle: {
    fontSize: 'clamp(42px, 8vw, 72px)',
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '1px',
    marginBottom: '32px',
    color: '#ffffff',
  },
  jumbotronSubtitle: {
    fontSize: 'clamp(18px, 3vw, 24px)',
    fontWeight: tokens.fontWeightRegular,
    marginBottom: '16px',
    color: '#ffffff',
    opacity: 0.9,
    lineHeight: '1.4',
  },
  jumbotronLead: {
    fontSize: tokens.fontSizeBase300,
    color: '#ffffff',
    opacity: 0.8,
    marginBottom: '32px',
  },
  getStartedButton: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    ...shorthands.padding('12px', '32px'),
    backgroundColor: tokens.colorBrandBackground,
    color: '#ffffff',
    ...shorthands.border('none'),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow16,
    },
  },
  content: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  sitesContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: tokens.spacingHorizontalXXL,
    paddingTop: '36px',
    paddingBottom: '40px',
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    maxWidth: '1400px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
  },
  siteCard: {
    cursor: 'pointer',
    height: '100%',
    ...shorthands.border('1px', 'solid', 'rgba(255,255,255,0.12)'),
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: tokens.shadow16,
      opacity: 0.95,
    },
  },
  cardBody: {
    ...shorthands.padding('24px'),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  cardIcon: {
    fontSize: '32px',
    color: tokens.colorNeutralForeground3,
  },
  cardTitle: {
    fontSize: '24px',
    fontWeight: '600',
    margin: 0,
    color: tokens.colorNeutralForeground1,
  },
  cardDescription: {
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.6',
  },
  footer: {
    marginTop: 'auto',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('8px', '16px'),
  },
  footerContent: {
    maxWidth: '1400px',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  footerSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  footerLabel: {
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground3,
  },
  footerValue: {
    color: tokens.colorNeutralForeground2,
    fontFamily: 'monospace',
  },
  footerSeparator: {
    color: tokens.colorNeutralStroke1,
    margin: '0 4px',
  },
  footerCopyright: {
    color: tokens.colorNeutralForeground3,
  },
});